import { CloseOutlined } from "@ant-design/icons";
import { Button, Modal, Progress, Spin, Typography } from "antd";
import Cookies from "js-cookie";
import { useEffect, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import apiFactory from "../../api";
import { CHUNK_SIZE, MESSAGE_STATUS } from "../../config/Constant";
import { useMenuContext } from "../../context/MenuContext";
import "./style.scss";
import { uploadFileV2 } from "../../utils/SendFile";


const PreviewFileUploadModal = ({
  isModalOpen,
  handleCancel,
  uploadFiles,
  setUploadFiles,
  setPreviewFiles,
}) => {
  const { selectedConversation } = useMenuContext();
  const [abortController, setAbortController] = useState({});
  const sharedAbortController = useRef(null);
  const fileUploading = useRef(null);
  const [progressState, setProgressState] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);

  const sendFile = async () => {
    const controller = new AbortController();
    sharedAbortController.current = controller;

    try {
      for (const file of uploadFiles) {
        fileUploading.current = file;

        const rs = await handleSendFileV2(file);

        if (controller.signal.aborted) {
          throw new Error("Aborted");
        }

        if (rs) {
          const fileConvert = {
            originalName: file?.name,
            pathUrl: rs?.path,
            resourceId: rs?.resourceId,
            volume: rs?.volume,
            videoImgResourceId: rs?.videoImgResourceId,
            previewVideoImage: rs?.previewVideoImage,
            duration: rs?.duration,
            uid: file?.uid,
          };

          setPreviewFiles((prev) => [...(prev || []), fileConvert]);
        }

        fileUploading.current = null;
      }
    } catch (error) {}
  };

  const handleDeleteFile = (uid) => {
    const fileToDelete = uploadFiles?.find((file) => file?.uid === uid);

    if (fileToDelete && abortController) {
      abortController.abort();

      const newUploadFilesV2 = uploadFiles?.filter((file) => file?.uid !== uid);
      setUploadFiles([...(newUploadFilesV2 || [])]);

      apiFactory.messageApi.cancelMessage({
        requestUuid: uid,
      });
    }
  };

  const handleSendFileV2 = async (file) => {
    const controller = new AbortController();
    setAbortController(controller);

    const reqUuid = file?.uid;
    const uploadFile = uploadFiles?.find((file) => file?.uid === reqUuid);
    if (!uploadFile) return false;

    const totalChunk = Math.ceil(uploadFile?.size / CHUNK_SIZE);
    try {
      const resource = await uploadFileV2(
        file,
        selectedConversation?.conversationId,
        "NOTIFICATION",
        controller.signal,
        (uploadedCount, chunkData) => {
          updateProgress(file, uploadedCount, totalChunk);
        },
      );

      const payload = {
        conversationId: selectedConversation?.conversationId || null,
        requestUuid: reqUuid,
        contentType: MESSAGE_STATUS.FILE,
        name: uploadFile?.name,
        token: Cookies.get("access_token"),
        totalVolume: uploadFile?.size,
        totalChunk: totalChunk,
        actualContentType: uploadFile?.type,
        resource: resource,
        folder: "NOTIFICATION",
      };

      const response = await apiFactory.messageApi.sendFile(payload);

      if (response?.status === 200) {
        fileUploading.current.isSuccess = true;
        return response?.data;
      } else {
        fileUploading.current.isSuccess = false;
        return null;
      }
    } catch (error) {
      console.error("ERR upload/send File:", error);
    }
  };

  const updateProgress = (file, uploaded, total) => {
    const percent = Math.round((uploaded / total) * 100);
    setProgressState((prev) => ({
      ...prev,
      [file.uid]: percent,
    }));
  };

  const getFileSize = (file) => {
    if (!file?.size) return "0 KB";

    const sizeInMB = file.size / (1024 * 1024);
    const sizeInKB = file.size / 1024;

    return sizeInMB >= 1
      ? `${sizeInMB.toFixed(1)} MB`
      : `${sizeInKB.toFixed(1)} KB`;
  };

  const handleCloseModal = () => {
    handleStopAllUploads();
    handleCancel();
    setUploadFiles([]);
    setProgressState({});
  };

  const handleStopAllUploads = () => {
    if (sharedAbortController.current && abortController) {
      abortController.abort();
      sharedAbortController.current.abort();
    }

    setUploadFiles([]);
  };

  useEffect(() => {
    if (uploadFiles?.length > 0 && !isUploading) {
      setIsLoading(false);
      setIsUploading(true);
      sendFile().finally(() => {
        setIsUploading(false);

        setTimeout(() => {
          handleCancel();
        }, 1000);
      });
    }
  }, [uploadFiles?.length]);

  return (
    <Modal
      title="Upload"
      open={isModalOpen}
      centered
      closable={false}
      maskClosable={false}
      footer={
        <div style={{ marginTop: 20, textAlign: "right" }}>
          <Button onClick={handleCloseModal}>Cancel</Button>
        </div>
      }
    >
      <Typography.Title level={5}>Attach attachments</Typography.Title>
      {!isLoading ? (
        uploadFiles?.map((file) => (
          <div key={file?.uid}>
            <div style={{ marginTop: 20 }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginTop: 10,
                }}
              >
                <Typography.Text>{file?.name}</Typography.Text>
                {fileUploading.current?.uid === file?.uid && (
                  <CloseOutlined
                    style={{ marginLeft: 10, cursor: "pointer" }}
                    onClick={() => handleDeleteFile(file?.uid)}
                  />
                )}
                <Typography.Text style={{ marginLeft: "auto" }}>
                  {getFileSize(file)}
                </Typography.Text>
              </div>

              <Progress percent={progressState[file?.uid] || 0} />
            </div>
          </div>
        ))
      ) : (
        <div className="w-full h-full items-center justify-center pt-5 pb-5">
          <Spin />
        </div>
      )}
    </Modal>
  );
};

export { PreviewFileUploadModal };
